import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import PresenceTracker from "@/components/PresenceTracker";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import Index from "./pages/Index";
import ProtectedRoute from "@/components/ProtectedRoute";

// SEO-critical pages: loaded eagerly so Googlebot can render them immediately
import Profilatura from "./pages/Profilatura";
import Assistenza from "./pages/Assistenza";
import Ricambi from "./pages/Ricambi";
import StampaDtf from "./pages/StampaDtf";
import StampaDtfUv from "./pages/StampaDtfUv";
import Categoria from "./pages/Categoria";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import TerminiCondizioni from "./pages/TerminiCondizioni";

// Non-SEO pages: lazy loaded for performance
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminMater = lazy(() => import("./pages/AdminMater"));
const Prodotto = lazy(() => import("./pages/Prodotto"));
const Carrello = lazy(() => import("./pages/Carrello"));
const Checkout = lazy(() => import("./pages/Checkout"));
const OrdineConfermato = lazy(() => import("./pages/OrdineConfermato"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const Preferiti = lazy(() => import("./pages/Preferiti"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <FavoritesProvider>
          <CartProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <PresenceTracker />
              <CookieConsentBanner />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/profilatura" element={<Profilatura />} />
                  <Route path="/assistenza" element={<Assistenza />} />
                  <Route path="/ricambi" element={<Ricambi />} />
                  <Route path="/stampa-dtf" element={<StampaDtf />} />
                  <Route path="/stampa-dtf-uv" element={<StampaDtfUv />} />
                  <Route path="/adminmater" element={<ProtectedRoute requiredRoles={["admin", "manager_shop"]}><AdminMater /></ProtectedRoute>} />
                  <Route path="/categoria/:slug" element={<Categoria />} />
                  <Route path="/prodotto/:slug" element={<Prodotto />} />
                  <Route path="/carrello" element={<Carrello />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/ordine-confermato" element={<OrdineConfermato />} />
                  <Route path="/payment-success" element={<PaymentSuccess />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/cookie-policy" element={<CookiePolicy />} />
                  <Route path="/termini-condizioni" element={<TerminiCondizioni />} />
                  <Route path="/preferiti" element={<Preferiti />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </CartProvider>
      </FavoritesProvider>
    </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
